import React, {useCallback} from "react";
import {useSupportBotContext} from "../SupportBotData";
import {
    Split,
    Info,
    GitBranch,
    GitBranchIcon,
    GitPullRequestDraftIcon,
    GitBranchPlus,
    BadgeInfo,
    CopyX
} from "lucide-react";
import {useIntl} from "react-intl";
import "./VersionDifferences.scss";
import {ReactComponent as VersionIcon} from "../../../svg/version.svg";
export const VersionDifferenceIndicator = ({differenceTitle}) => {
    const intl = useIntl()
    const title = differenceTitle || intl.formatMessage({
        defaultMessage: "Different from default version",
        id: "differentVersionFromDefault"
    })
    return <span className="version-difference-indicator" title={title}>
       <BadgeInfo />
    </span>
}

export const useVersionDifferences = () => {
    const { data: { supportBots, supportTopicCodes } } = useSupportBotContext();

    const isDefaultBot = useCallback((currentBot) => {
        // Explicitly named "DEFAULT" or has no version set
        if (!currentBot.config.version || currentBot.config.version === "DEFAULT") {
            return true;
        }

        // If no version group, it's a standalone bot and should be treated as default
        if (!currentBot.config.versionGroup) return true;

        // Check if it's the only bot in its version group
        if (supportBots) {
            const botsInSameGroup = supportBots.filter(bot =>
                bot.config?.versionGroup === currentBot.config.versionGroup
            );

            // If it's the only bot in its group, treat it as default
            if (botsInSameGroup.length <= 1) {
                return true;
            }
        }

        return false;
    }, [supportBots]);

    const findDefaultBot = useCallback((currentBot) => {
        // If this is already the default version, nothing is different
        if (isDefaultBot(currentBot)) {
            return false;
        }

        if (!currentBot?.config?.versionGroup || !supportBots) return null;


        // Find the default "DEFAULT" bot with the same version group
        return supportBots.find(bot =>
            bot.config?.versionGroup === currentBot.config.versionGroup &&
            (bot.config?.version === "DEFAULT" || !bot.config?.version)
        );
    }, [supportBots, isDefaultBot]);

    const isBotFieldDifferent = useCallback((currentBot, fieldPath) => {
        // If this is already the default version, nothing is different
        if (isDefaultBot(currentBot)) {
            return false;
        }

        const defaultBot = findDefaultBot(currentBot);
        if (!defaultBot) return false;

        // Get the field value using path notation
        const getNestedValue = (obj, path) => {
            return path.split('.').reduce((prev, curr) =>
                prev && prev[curr] !== undefined ? prev[curr] : undefined, obj);
        };

        const currentValue = getNestedValue(currentBot, fieldPath);
        const defaultValue = getNestedValue(defaultBot, fieldPath);

        // Handle arrays (like supportTopics or materials)
        if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
            // Check length first
            if (currentValue.length !== defaultValue.length) return true;

            // Check if all items match
            for (let i = 0; i < currentValue.length; i++) {
                if (!defaultValue.includes(currentValue[i])) return true;
            }

            return false;
        }

        // Regular value comparison
        return currentValue !== defaultValue;
    }, [findDefaultBot, isDefaultBot]);

    const isTopicFieldDifferent = useCallback((topicId, fieldName, currentValue, botData) => {
        // If this is already the default version, nothing is different
        if (isDefaultBot({ config: botData })) {
            return false;
        }

        if (!supportTopicCodes || !topicId || !fieldName) return false;

        const topicBase = supportTopicCodes[topicId];
        if (!topicBase) return false;

        // Compare with the base value (excluding versions)
        return currentValue !== topicBase[fieldName];
    }, [supportTopicCodes, isDefaultBot]);

    const isTopicAddedFromDefault = useCallback((currentBot, topicId) => {
        // If this is a default bot, nothing is different
        if (isDefaultBot(currentBot)) {
            return false;
        }

        const defaultBot = findDefaultBot(currentBot);
        if (!defaultBot?.config?.supportTopics) return false;

        return !defaultBot.config.supportTopics.includes(topicId);
    }, [findDefaultBot, isDefaultBot]);

    const getMissingTopics = useCallback((currentBot) => {
        // If this is a default bot, nothing is missing
        if (isDefaultBot(currentBot)) {
            return [];
        }

        const defaultBot = findDefaultBot(currentBot);
        if (!defaultBot?.config?.supportTopics || !currentBot?.config?.supportTopics) return [];

        return defaultBot.config.supportTopics.filter(
            topicId => !currentBot.config.supportTopics.includes(topicId)
        );
    }, [findDefaultBot, isDefaultBot]);

    const getMissingMaterials = useCallback((currentBot) => {
        // If this is a default bot, nothing is missing
        if (isDefaultBot(currentBot)) {
            return [];
        }
        const defaultBot = findDefaultBot(currentBot);
        if (!defaultBot?.config?.materials || !currentBot?.config?.materials) return [];

        return defaultBot.config.materials.filter(defaultMaterial => {
            const isDefaultLegacy = typeof defaultMaterial === 'string';
            const defaultId = isDefaultLegacy ? null : defaultMaterial.id;

            return !currentBot.config.materials.some(currentMaterial => {
                const isCurrentLegacy = typeof currentMaterial === 'string';
                const currentId = isCurrentLegacy ? null : currentMaterial.id;

                if (isDefaultLegacy !== isCurrentLegacy) {
                    return false;
                }

                if (defaultId && currentId) {
                    return currentId === defaultId;
                }

                if (isDefaultLegacy && isCurrentLegacy) {
                    return currentMaterial === defaultMaterial;
                }

                return false;
            });
        });
    }, [findDefaultBot, isDefaultBot]);

    return {
        isBotFieldDifferent,
        isTopicFieldDifferent,
        isTopicAddedFromDefault,
        getMissingTopics,
        getMissingMaterials,
        findDefaultBot
    };
};